import PIXI from 'pixi.js/src';
import config from './../config/config';
import renderHelper from './../helpers/render';
import stephCurry from './../graphics/steph-curry';
import data from './../data/data';
import basket from './../graphics/basket';
import ball from './../graphics/ball';
import explosion from './../graphics/explosion';
import splash from './../graphics/splash';
import statsView from './stats';
import timer from './../lib/timer';
import router from './../router/router';
import GameStore from './../stores/GameStore';


const gameView = {
  addNextBasketTimeout: 0,
  addNextBasketInterval: 0,
  addNextBasketTimer: 0,
  active: false,
  allBaskets: [],
  allBalls: [],
  allExplosions: [],
  allSplashes: [],
  paused: false,
  backgroundWidth: 327,
  backgroundHeight: 492,
  backgroundSpriteArray: [],
  specialBasketChances: 7,


  /**
   * Sets up the view
   * @returns {undefined} undefined
   */
  init() {
    this.currentLevelData = GameStore.getState().currentLevelData;
    this.graphicsContainer = new PIXI.Container();
    renderHelper.stage.addChild(this.graphicsContainer);

    this.backgroundSquare = new PIXI.Graphics();
    this.backgroundSquare.beginFill(this.currentLevelData.backgroundColor);
    this.backgroundSquare.drawRect(
      0,
      0,
      window.innerWidth,
      window.innerHeight);
    this.graphicsContainer.addChild(this.backgroundSquare);

    this.showLevelGoals();
    this.countdownFunction = this.countDown.bind(this);
    this.hideGoalsInterval = setInterval(this.countdownFunction, 1000);
  },


  /**
   * Shows the goals for the level
   * @returns {undefined} undefined
   */
  showLevelGoals() {
    this.goalsContainer = new PIXI.Container();
    renderHelper.stage.addChild(this.goalsContainer);

    this.goalsText = new PIXI.Text('MAKE ' +
      this.currentLevelData.minimumShots + ' SHOTS \nAT ' +
      (this.currentLevelData.minimumPercentage * 100) + '%',
      {
        font: '18px ' + config.fontName,
        fill: config.whiteColor,
        align: 'center',
      }
    );
    this.goalsText.resolution = 2;

    this.countdownText = new PIXI.Text('3',
      {
        font: '24px ' + config.fontName,
        fill: config.whiteColor,
        align: 'center',
      }
    );
    this.countdownText.resolution = 2;

    this.goalsContainer.addChild(this.goalsText);
    this.goalsContainer.addChild(this.countdownText);

    this.positionGraphics();
  },


  /**
   * Counts down to gameplay
   * @returns {undefined} undefined
   */
  countDown() {
    const curTime = Number(this.countdownText.text);
    const newTime = curTime - 1;

    this.countdownText.text = newTime;

    if (newTime === 0) {
      clearInterval(this.hideGoalsInterval);
      this.goalsContainer.removeChild(this.goalsText);
      this.countdownText.text = 'GO!';
      this.countdownText.x = (window.innerWidth / 2) -
        (this.countdownText.width / 2);
      this.countdownText.y = (window.innerHeight / 2) -
        (this.countdownText.height / 2);
      this.startFunction = this.start.bind(this);
      this.startGameTimeout = setTimeout(this.startFunction, 1000);
    }
  },


  /**
   * Hides the goals for the level
   * @returns {undefined} undefined
   */
  hideGoals() {
    renderHelper.stage.removeChild(this.goalsContainer);
  },


  /**
   * Starts the gameplay
   * @returns {undefined} undefined
   */
  start() {
    this.hideGoals();
    this.createBackground();

    this.active = true;

    this.stephCurry = Object.assign({}, stephCurry);
    this.stephCurry.init();

    this.explosion = Object.assign({}, explosion);
    this.explosion.init();

    this.statsView = Object.assign({}, statsView);
    this.statsView.init();
    this.statsView.updateTime(this.currentLevelData.time);

    this.splash = Object.assign({}, splash);
    this.splash.init();

    this.addNextBasketInterval = 30;
    this.addNextBasketTimeout = 0;
    this.storeChangeListener = this.onGameStoreChange.bind(this);

    GameStore.addChangeListener(this.storeChangeListener);
    this.resizeListener = this.onResize.bind(this);
    window.addEventListener('resize', this.resizeListener);
    window.addEventListener('orientationchange', this.resizeListener);

    this.positionGraphics();

    timer.start(this.currentLevelData.time,
      this.onTimeTick.bind(this),
      this.onTimeUp.bind(this));

    this.addBasket();
  },


  /**
   * Adds background tiles
   * @returns {undefined} undefined
   */
  createBackground() {
    const backgroundTileCount = Math.ceil(window.innerWidth /
      this.backgroundWidth);

    for (let i = 0; i < backgroundTileCount; i++) {
      const backgroundSprite = new PIXI.Sprite(
        PIXI.loader.resources[data.backgroundImage].texture
      );

      backgroundSprite.width = this.backgroundWidth;
      backgroundSprite.height = this.backgroundHeight;
      backgroundSprite.x = i * this.backgroundWidth;
      this.graphicsContainer.addChild(backgroundSprite);

      this.backgroundSpriteArray.push(backgroundSprite);
    }
  },


  /**
   * Orientation change handler
   * @returns {undefined} undefined
   */
  onResize() {
    this.positionGraphics();
  },


  /**
   * Positions graphics
   * @returns {undefined} undefined
   */
  positionGraphics() {
    for (let i = 0; i < this.backgroundSpriteArray.length; i++) {
      this.backgroundSpriteArray[i].y =
        Math.round(window.innerHeight - this.backgroundHeight);
    }

    if (this.goalsText) {
      this.goalsText.x = Math.round((window.innerWidth / 2) - (this.goalsText.width / 2));
      this.goalsText.y = Math.round((window.innerHeight / 2) -
        (this.goalsText.height / 2) - (this.countdownText.height - 2));

      this.countdownText.x = Math.round((window.innerWidth / 2) - (this.countdownText.width / 2));
      this.countdownText.y = Math.round(this.goalsText.y + this.goalsText.height + 40);
    }
  },


  /**
   * Starts animating in baskets
   * @returns {undefined} undefined
   */
  addBasket() {
    const _this = this;
    this.addNextBasketTimeout++;

    if (!this.paused) {
      const newBasket = Object.assign({}, basket);
      const nextBasketInterval = 80 - (Math.round(Math.random() * 20));
      const state = GameStore.getState();
      const isSpecialInt = Math.floor(Math.random() * this.specialBasketChances);
      const createSpecial = state.currentLevelData.level > 1 &&
        isSpecialInt === (this.specialBasketChances - 1);

      this.addNextBasketInterval = nextBasketInterval;
      this.addNextBasketTimeout = 0;
      newBasket.init();
      newBasket.create(this.currentLevelData.level);
      this.allBaskets.push(newBasket);

      if (createSpecial) {
        const specialBasket = Object.assign({}, basket);
        specialBasket.init();
        specialBasket.create(_this.currentLevelData.level, true);
        _this.allBaskets.push(specialBasket);
      }
    }

    if (this.active) {
      const randBasketTime = 900 + (Math.random() * 1000);

      this.addNextBasketTimeout = setTimeout(() => {
        _this.addBasket();
      }, randBasketTime);
    }
  },


  /**
   * Called when a CHANGE event is emitted from the GameStore
   * @returns {undefined} undefined
   */
  onGameStoreChange() {
    const state = GameStore.getState();

    if (state.shootingBall) {
      const newBall = Object.assign({}, ball);
      newBall.init();

      newBall.create(this.currentLevelData.level,
        state.ballXPosition,
        state.ballVelocity,
        state.onFire);
      this.allBalls.push(newBall);
    }

    if (state.ballToRemove) {
      let newExplosionObject;
      const offset = {
        x: 7,
        y: 7,
      };

      if (state.onFire) {
        newExplosionObject = Object.assign({}, this.explosion);
        offset.x = 0;
        this.allExplosions.push(newExplosionObject);
      } else {
        newExplosionObject = Object.assign({}, this.splash);
        this.allSplashes.push(newExplosionObject);
      }

      newExplosionObject.create(state.basketToRemove.x + offset.x,
        state.basketToRemove.y - offset.y);
    }

    if (state.paused) {
      clearTimeout(this.addNextBasketTimeout);

      if (!this.paused) {
        this.paused = true;
        timer.pause();
      }
    } else {
      if (this.paused) {
        this.paused = false;
        timer.unPause();
      }
    }
  },


  /**
   * Called when the time is ticking for the current level
   * @param {Number} time - The current time
   * @returns {undefined} undefined
   */
  onTimeTick(time) {
    const remainingTime = this.currentLevelData.time - time;
    this.statsView.updateTime(remainingTime);
  },


  /**
   * Called when the time is up for the current level
   * @returns {undefined} undefined
   */
  onTimeUp() {
    const state = GameStore.getState();
    const levelData = this.currentLevelData;
    const levelStats = state.levelStats['level' + this.currentLevelData.level];
    let levelPassed = 1;

    this.active = false;

    if (!levelStats || levelStats.score < levelData.minimumShots ||
      levelStats.percentageMade < levelData.minimumPercentage) {
      levelPassed = 0;
    }

    if (levelPassed) {
      if (this.currentLevelData.level < data.levelData.length) {
        router.navigate('interstitial-' + this.currentLevelData.level,
          { level: this.currentLevelData.level });
      } else {
        router.navigate('end');
      }
    } else {
      router.navigate('game-over');
    }

    timer.stop();
  },


  /**
   * Destroys the view
   * @returns {undefined} undefined
   */
  destroy() {
    clearInterval(this.hideGoalsInterval);
    clearTimeout(this.startGameTimeout);
    clearInterval(this.addNextBasketInterval);
    clearTimeout(this.addNextBasketTimeout);

    renderHelper.stage.removeChild(this.graphicsContainer);
    renderHelper.stage.removeChild(this.goalsContainer);
    this.active = false;
    GameStore.removeChangeListener(this.storeChangeListener);
    window.removeEventListener('resize', this.resizeListener);
    window.removeEventListener('orientationchange', this.resizeListener);
    this.stephCurry.destroy();
    this.statsView.destroy();

    for (let i = 0; i < this.allBalls.length; i++) {
      this.allBalls[i].destroy();
    }

    for (let i = 0; i < this.allBaskets.length; i++) {
      this.allBaskets[i].destroy();
    }

    for (let i = 0; i < this.allExplosions.length; i++) {
      this.allExplosions[i].destroy();
    }

    for (let i = 0; i < this.allSplashes.length; i++) {
      this.allSplashes[i].destroy();
    }

    this.allBaskets = [];
    this.allBalls = [];
    this.allExplosions = [];
    this.allSplashes = [];
  },
};

export default gameView;



/** WEBPACK FOOTER **
 ** ./assets/js/src/views/game.js
 **/